/**
 * The CS HUD stylesheet, injected as one <style> element by init() and removed
 * by dispose(). No CSS pipeline is configured for new entries, so the CSS lives
 * here as a template string.
 *
 * Design system
 * -------------
 *   scale    every dimension is `calc(Npx * var(--k))`; --k is height/1080 set
 *            from resize(). The HUD holds proportion from 720p to 1440p+.
 *   type     condensed uppercase, tabular figures, generous letter-spacing.
 *   ink      three levels (95% / 62% / 32%) plus one accent per team:
 *            T = sandy amber, CT = steel blue. No gradients, no big scrims.
 *   motion   no @keyframes for gameplay feedback — every animated value is
 *            integrated in update() so a paused or pump-driven frame freezes
 *            with the rest of the engine.
 */

const CSS = `
#ducs-cs{
  position:fixed; inset:0; z-index:60; pointer-events:none;
  --k:1;
  --t:#d9a441; --ct:#5b8dd6;
  --ink:#ececec; --ink2:rgba(236,236,236,.62); --ink3:rgba(236,236,236,.30);
  --warn:#e0392b; --ok:#8fd14f;
  --hair:rgba(255,255,255,.16);
  --panel:rgba(10,12,14,.86);
  font-family:'DIN Alternate','Oswald','Arial Narrow',system-ui,sans-serif;
  font-weight:600; letter-spacing:.08em; text-transform:uppercase;
  font-variant-numeric:tabular-nums; color:var(--ink);
  text-shadow:0 calc(1px*var(--k)) calc(2px*var(--k)) rgba(0,0,0,.92);
  -webkit-font-smoothing:antialiased;
  user-select:none; -webkit-user-select:none;
}
#ducs-cs *{margin:0;padding:0;box-sizing:border-box}
#ducs-cs i,#ducs-cs b,#ducs-cs em{font-style:normal;font-weight:600}
#ducs-cs .hidden{display:none}

/* ───────────────────────────── round info bar ─────────────────────────── */
.cs-round{
  position:absolute; left:50%; top:calc(10px*var(--k));
  transform:translateX(-50%);
  display:flex; align-items:center; gap:calc(10px*var(--k));
  padding:calc(4px*var(--k)) calc(12px*var(--k));
  background:var(--panel); border:1px solid var(--hair);
}
.cs-round .cs-score{
  font-size:calc(20px*var(--k)); line-height:1; min-width:calc(22px*var(--k));
  text-align:center;
}
.cs-round .cs-team-t{color:var(--t)}
.cs-round .cs-team-ct{color:var(--ct)}
.cs-round .cs-mid{display:flex; flex-direction:column; align-items:center; gap:calc(2px*var(--k))}
.cs-round .cs-timer{font-size:calc(24px*var(--k)); line-height:1; letter-spacing:.04em}
.cs-round .cs-timer.hot{color:var(--warn)}
.cs-round .cs-sites{display:flex; gap:calc(4px*var(--k))}
.cs-round .cs-site{
  font-size:calc(10px*var(--k)); line-height:1; padding:calc(2px*var(--k)) calc(5px*var(--k));
  color:var(--ink3); border:1px solid transparent;
}
.cs-round .cs-site.on{color:var(--t); border-color:var(--t)}

/* ───────────────────────────── bomb ───────────────────────────────────── */
.cs-bombwrap{
  position:absolute; left:50%; top:calc(64px*var(--k));
  transform:translateX(-50%);
  display:flex; flex-direction:column; align-items:center; gap:calc(6px*var(--k));
}
.cs-fuse{display:none; align-items:center; gap:calc(8px*var(--k));
  padding:calc(5px*var(--k)) calc(12px*var(--k));
  background:var(--panel); border:1px solid rgba(224,57,43,.55);
  flex-direction:column; min-width:calc(150px*var(--k));
}
.cs-fuse.on{display:flex}
.cs-fuse .cs-fuse-top{display:flex; align-items:center; gap:calc(8px*var(--k))}
.cs-fuse .cs-fuse-ico{
  font-size:calc(11px*var(--k)); padding:calc(2px*var(--k)) calc(4px*var(--k));
  background:var(--warn); color:#fff; letter-spacing:.1em;
}
.cs-fuse .cs-fuse-time{font-size:calc(22px*var(--k)); line-height:1}
.cs-fuse .cs-fuse-bar{
  width:100%; height:calc(2px*var(--k)); background:rgba(255,255,255,.14);
}
.cs-fuse .cs-fuse-bar i{display:block; height:100%; width:0; background:var(--warn)}
.cs-fuse.danger .cs-fuse-time{color:var(--warn)}
.cs-fuse.danger{border-color:var(--warn)}

.cs-carry{
  display:none; align-items:center; gap:calc(6px*var(--k));
  padding:calc(3px*var(--k)) calc(9px*var(--k));
  background:var(--panel); border:1px solid var(--hair);
  font-size:calc(11px*var(--k)); color:var(--t);
}
.cs-carry.on{display:flex}

.cs-prog{display:none; flex-direction:column; align-items:center; gap:calc(3px*var(--k));
  min-width:calc(170px*var(--k))}
.cs-prog.on{display:flex}
.cs-prog .cs-prog-label{font-size:calc(11px*var(--k)); color:var(--ink2)}
.cs-prog .cs-prog-bar{
  width:100%; height:calc(4px*var(--k)); background:rgba(255,255,255,.14);
  border:1px solid var(--hair);
}
.cs-prog .cs-prog-bar i{display:block; height:100%; width:0; background:var(--ink)}
.cs-prog.defuse .cs-prog-bar i{background:var(--ok)}

/* ───────────────────────────── vitals ─────────────────────────────────── */
.cs-vitals{
  position:absolute; left:calc(20px*var(--k)); bottom:calc(18px*var(--k));
  display:flex; flex-direction:column; gap:calc(5px*var(--k));
}
.cs-stat{display:flex; align-items:center; gap:calc(7px*var(--k))}
.cs-stat .cs-ico{
  font-size:calc(15px*var(--k)); width:calc(14px*var(--k)); text-align:center; color:var(--ink2);
}
.cs-stat .cs-val{font-size:calc(28px*var(--k)); line-height:1; min-width:calc(46px*var(--k))}
.cs-stat .cs-bar{width:calc(92px*var(--k)); height:calc(3px*var(--k)); background:rgba(255,255,255,.14)}
.cs-stat .cs-bar i{display:block; height:100%; width:0; background:var(--ink)}
.cs-stat.armor .cs-bar i{background:var(--ct)}
.cs-stat.armor{opacity:.42}
.cs-stat.armor.on{opacity:1}
.cs-stat.low .cs-val{color:var(--warn)}
.cs-stat.low .cs-bar i{background:var(--warn)}

/* ───────────────────────────── money + ammo ───────────────────────────── */
.cs-cash{
  position:absolute; right:calc(20px*var(--k)); bottom:calc(18px*var(--k));
  display:flex; flex-direction:column; align-items:flex-end; gap:calc(3px*var(--k));
}
.cs-cash .cs-money{display:flex; align-items:baseline; gap:calc(2px*var(--k))}
.cs-cash .cs-cur{font-size:calc(17px*var(--k)); color:var(--ink2)}
.cs-cash .cs-amt{font-size:calc(30px*var(--k)); line-height:1}
.cs-cash .cs-ammo{display:flex; align-items:baseline; gap:calc(4px*var(--k))}
.cs-cash .cs-mag{font-size:calc(24px*var(--k)); line-height:1}
.cs-cash .cs-slash{font-size:calc(15px*var(--k)); color:var(--ink3)}
.cs-cash .cs-res{font-size:calc(17px*var(--k)); color:var(--ink2)}
.cs-cash .cs-wname{font-size:calc(11px*var(--k)); color:var(--ink2); margin-left:calc(6px*var(--k))}
.cs-cash.empty .cs-mag{color:var(--warn)}

/* ───────────────────────────── killfeed ───────────────────────────────── */
.cs-kf{
  position:absolute; right:calc(20px*var(--k)); top:calc(14px*var(--k));
  display:flex; flex-direction:column; align-items:flex-end; gap:calc(3px*var(--k));
  max-width:calc(440px*var(--k));
}
.cs-kf-row{
  display:none; align-items:center; gap:calc(6px*var(--k));
  padding:calc(3px*var(--k)) calc(7px*var(--k));
  background:var(--panel); border-left:calc(2px*var(--k)) solid var(--hair);
  font-size:calc(12px*var(--k)); line-height:1.25;
}
.cs-kf-row.on{display:flex}
.cs-kf-row .cs-kf-k,.cs-kf-row .cs-kf-v{white-space:nowrap; max-width:calc(150px*var(--k));
  overflow:hidden; text-overflow:ellipsis}
.cs-kf-row .cs-kf-w{color:var(--ink2); font-size:calc(11px*var(--k))}
.cs-kf-row .cs-kf-hs{
  display:none; width:calc(9px*var(--k)); height:calc(9px*var(--k)); border-radius:50%;
  border:1px solid var(--warn); position:relative;
}
.cs-kf-row .cs-kf-hs.on{display:block}
.cs-kf-row .cs-kf-hs::after{
  content:''; position:absolute; left:50%; top:50%; width:40%; height:40%;
  transform:translate(-50%,-50%); background:var(--warn); border-radius:50%;
}

/* ───────────────────── centre: hitmarker + damage arc ─────────────────── */
.cs-centre{position:absolute; left:50%; top:50%; width:0; height:0}

.cs-hm{position:absolute; left:0; top:0; width:0; height:0; opacity:0}
.cs-hm i{
  position:absolute; left:calc(-1px*var(--k)); top:0;
  width:calc(2px*var(--k)); height:calc(11px*var(--k)); background:#fff;
  transform-origin:50% 0;
}
.cs-hm i:nth-child(1){transform:rotate(45deg) translate(0,calc(7px*var(--k)))}
.cs-hm i:nth-child(2){transform:rotate(135deg) translate(0,calc(7px*var(--k)))}
.cs-hm i:nth-child(3){transform:rotate(225deg) translate(0,calc(7px*var(--k)))}
.cs-hm i:nth-child(4){transform:rotate(315deg) translate(0,calc(7px*var(--k)))}
.cs-hm.hs i{background:#ff5a45; height:calc(13px*var(--k))}
.cs-hm.kill i{background:#ff3b2f; height:calc(15px*var(--k)); width:calc(3px*var(--k))}

.cs-dmg{position:absolute; left:0; top:0; width:0; height:0; opacity:0}
.cs-dmg i{
  position:absolute; display:block;
  left:calc(-70px*var(--k)); bottom:calc(46px*var(--k));
  width:calc(140px*var(--k)); height:calc(70px*var(--k));
  border:calc(4px*var(--k)) solid transparent;
  border-top-color:rgba(255,64,48,.92);
  border-radius:50%;
}

/* ───────────────────────────── scoreboard ─────────────────────────────── */
.cs-sb{
  position:absolute; inset:0; display:none; align-items:center; justify-content:center;
  pointer-events:none;
}
.cs-sb.on{display:flex}
.cs-sb-box{
  pointer-events:auto; width:calc(760px*var(--k)); max-width:82vw;
  background:rgba(8,10,12,.90); border:1px solid var(--hair);
  padding:calc(14px*var(--k)) calc(16px*var(--k));
  text-shadow:none;
}
.cs-sb-head{
  display:flex; align-items:baseline; justify-content:space-between;
  border-bottom:1px solid var(--hair); padding-bottom:calc(7px*var(--k));
}
.cs-sb-head .cs-sb-map{font-size:calc(13px*var(--k)); color:var(--ink2)}
.cs-sb-head .cs-sb-score{font-size:calc(20px*var(--k)); display:flex; gap:calc(8px*var(--k))}
.cs-sb-head .t{color:var(--t)}
.cs-sb-head .ct{color:var(--ct)}
.cs-sb-team{
  display:flex; align-items:baseline; justify-content:space-between;
  margin-top:calc(11px*var(--k)); font-size:calc(12px*var(--k)); color:var(--ink2);
}
.cs-sb-team.t .cs-sb-tname{color:var(--t)}
.cs-sb-team.ct .cs-sb-tname{color:var(--ct)}
.cs-sb-team .cs-sb-tname{font-size:calc(13px*var(--k))}
.cs-sb-cols, .cs-sb-row{
  display:flex; align-items:center; gap:calc(8px*var(--k));
  font-size:calc(12px*var(--k)); height:calc(20px*var(--k));
}
.cs-sb-cols{color:var(--ink3); font-size:calc(9px*var(--k))}
.cs-sb-row{background:rgba(255,255,255,.03); margin-top:calc(2px*var(--k))}
.cs-sb-row:nth-child(even){background:rgba(255,255,255,.06)}
.cs-sb-row.dead{opacity:.42}
.cs-sb-row .cs-sb-alive{
  width:calc(6px*var(--k)); height:calc(6px*var(--k)); border-radius:50%;
  background:var(--ok); flex:0 0 auto;
}
.cs-sb-row.dead .cs-sb-alive{background:var(--warn)}
.cs-sb-row .cs-sb-name{flex:1 1 auto; overflow:hidden; text-overflow:ellipsis; white-space:nowrap}
.cs-sb-row .cs-sb-k,.cs-sb-row .cs-sb-d,.cs-sb-row .cs-sb-p{
  width:calc(34px*var(--k)); text-align:right; color:var(--ink2); flex:0 0 auto;
}
.cs-sb-row .cs-sb-k{color:var(--ink)}
.cs-sb-row .cs-sb-money{
  width:calc(64px*var(--k)); text-align:right; color:var(--ink2); flex:0 0 auto;
  display:flex; justify-content:flex-end; gap:calc(1px*var(--k));
}
.cs-sb-row .cs-sb-bomb{
  width:calc(20px*var(--k)); text-align:center; font-size:calc(8px*var(--k));
  color:var(--t); flex:0 0 auto; visibility:hidden;
}
.cs-sb-row .cs-sb-bomb.on{visibility:visible}

/* ───────────────────────────── buy menu ───────────────────────────────── */
.cs-buy{
  position:absolute; inset:0; display:none; align-items:center; justify-content:center;
  pointer-events:none;
}
.cs-buy.on{display:flex}
.cs-buy-panel{
  pointer-events:auto; width:calc(680px*var(--k)); max-width:86vw;
  background:rgba(8,10,12,.94); border:1px solid var(--hair);
  padding:calc(14px*var(--k)) calc(16px*var(--k)); text-shadow:none;
}
.cs-buy-head{display:flex; align-items:baseline; justify-content:space-between;
  border-bottom:1px solid var(--hair); padding-bottom:calc(8px*var(--k))}
.cs-buy-head .cs-buy-title{font-size:calc(16px*var(--k))}
.cs-buy-head .cs-buy-money{font-size:calc(18px*var(--k)); display:flex; gap:calc(2px*var(--k))}
.cs-buy-head .cs-buy-money .cs-cur{color:var(--ink2); font-size:calc(13px*var(--k))}
.cs-buy-grid{
  display:grid; grid-template-columns:repeat(3,1fr); gap:calc(6px*var(--k));
  margin-top:calc(11px*var(--k));
}
.cs-buy-item{
  display:none; align-items:center; gap:calc(7px*var(--k));
  padding:calc(6px*var(--k)) calc(8px*var(--k));
  background:rgba(255,255,255,.05); border:1px solid transparent;
  color:var(--ink); font:inherit; letter-spacing:inherit; text-transform:inherit;
  text-align:left; cursor:pointer; pointer-events:auto;
}
.cs-buy-item.on{display:flex}
.cs-buy-item:hover{background:rgba(255,255,255,.12); border-color:var(--hair)}
.cs-buy-item .cs-buy-slot{
  width:calc(15px*var(--k)); text-align:center; font-size:calc(10px*var(--k));
  color:var(--ink3); border:1px solid var(--hair);
}
.cs-buy-item .cs-buy-name{flex:1 1 auto; font-size:calc(12px*var(--k));
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap}
.cs-buy-item .cs-buy-cost{font-size:calc(12px*var(--k)); color:var(--ink2)}
.cs-buy-item.poor{opacity:.38; cursor:not-allowed}
.cs-buy-item.poor .cs-buy-cost{color:var(--warn)}
.cs-buy-foot{margin-top:calc(11px*var(--k)); font-size:calc(10px*var(--k)); color:var(--ink3)}

/* ───────────────────────── overlay / phase / net ──────────────────────── */
.cs-ov{position:absolute; inset:0; display:flex; flex-direction:column;
  align-items:center; gap:calc(8px*var(--k)); padding-top:calc(96px*var(--k))}
.cs-ov-phase{display:none; font-size:calc(30px*var(--k)); letter-spacing:.22em; color:var(--ink2)}
.cs-ov-phase.on{display:block}
.cs-ov-win{display:none; flex-direction:column; align-items:center; gap:calc(2px*var(--k))}
.cs-ov-win.on{display:flex}
.cs-ov-win .cs-ov-team{font-size:calc(40px*var(--k)); letter-spacing:.16em}
.cs-ov-win .cs-ov-sub{font-size:calc(13px*var(--k)); color:var(--ink2)}
.cs-ov-win.t .cs-ov-team{color:var(--t)}
.cs-ov-win.ct .cs-ov-team{color:var(--ct)}
.cs-ov-net{display:none; font-size:calc(11px*var(--k)); color:var(--ink2); align-items:center;
  gap:calc(6px*var(--k))}
.cs-ov-net.on{display:flex}
.cs-ov-net .cs-ov-ping{color:var(--ink3)}
.cs-ov-toast{
  display:none; margin-top:calc(6px*var(--k)); padding:calc(4px*var(--k)) calc(10px*var(--k));
  background:var(--panel); border:1px solid var(--hair);
  font-size:calc(12px*var(--k)); color:var(--ink2);
}
.cs-ov-toast.on{display:block}

/* ───────────────────────── touch + orientation ─────────────────────────── */

/* On touch devices the bottom corners belong to the virtual pad. The fire
   button is a fixed 92 px, so the offset must be fixed px too — a --k-scaled
   value would be ~40 px on a phone and would still overlap. */
@media (hover:none) and (pointer:coarse){
  .cs-cash{bottom:calc(124px + 18px*var(--k))}
  .cs-vitals{bottom:calc(96px + 18px*var(--k))}
}

/* Portrait: the screen is narrow, so pull the corner panels in and shrink the
   killfeed before it can reach the middle of the display. */
@media (orientation:portrait){
  .cs-kf{max-width:calc(240px*var(--k))}
  .cs-cash, .cs-vitals{transform:scale(.92); transform-origin:bottom}
}
`;

export function installStyles() {
  if (document.getElementById('ducs-cs-style')) return;
  const s = document.createElement('style');
  s.id = 'ducs-cs-style';
  s.textContent = CSS;
  document.head.appendChild(s);
}

export function removeStyles() {
  document.getElementById('ducs-cs-style')?.remove();
}

export { CSS };
